import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import type { Member } from '../actions/getMembers';
import { sendAdminRequest, sendRequest } from '../api/websocketApi';
import useKits from '../hooks/useKits';
import { ReactComponent as ReportsIcon } from '../icons/reports2.svg';
import {
    File,
    KitFilesObject,
    getKitById,
    getKitDisplayName,
} from '../pages/MemberPage';
import useStore from '../store/store';
import FileCard from './FileCard';
import Modal from './Modal';

interface ReportsAndRawDataModalProps {
    open: boolean;
    onClose: () => void;
    member: Member;
    isAdmin?: boolean;
}

const ReportsAndRawDataModal = ({
    open,
    onClose,
    member,
    isAdmin = false,
}: ReportsAndRawDataModalProps) => {
    const classes = useStyles();
    const kits = useKits({ memberId: member.MemberId, isAdmin: isAdmin });
    const [kitFiles, setKitFiles] = React.useState<KitFilesObject[]>([]);
    const kitTypes = useStore((state) => state.kitTypes);

    React.useEffect(() => {
        interface APIResponse {
            success: boolean;
            errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
            errDetails?: string;
            reports: File[];
            rawDataFiles: File[];
        }

        if (!kits?.length) return;

        const newFiles: KitFilesObject[] = [];
        let pendingRequests = kits.length;

        const handleResponse = (kitId: number) => (response: APIResponse) => {
            if (response.success && response.reports && response.rawDataFiles) {
                newFiles.push({
                    KitId: kitId,
                    Reports: response.reports,
                    RawDataFiles: response.rawDataFiles,
                });
            }

            pendingRequests--;
            if (pendingRequests === 0) {
                setKitFiles(newFiles);
            }
        };

        for (const kit of kits) {
            const config = {
                endpoint: '/kit/get-files',
                body: {
                    kitId: kit.KitId,
                },
            };
            if (isAdmin) {
                sendAdminRequest(config, handleResponse(kit.KitId));
            } else {
                sendRequest(config, handleResponse(kit.KitId));
            }
        }
    }, [kits, isAdmin]);

    return (
        <Modal
            open={open}
            title="Reports and Raw Data"
            onClose={onClose}
            icon={<ReportsIcon />}
            maxWidth="md"
        >
            <div className={classes.patientInfo}>
                <h3 className={classes.h3}>
                    {member.FirstName} {member.LastName}
                </h3>
                <div className={classes.details}>
                    <span>
                        <span className={classes.label}>Gender:</span>{' '}
                        <span className={classes.data}>{member.Sex}</span>
                    </span>
                    <span>
                        <span className={classes.label}>Birth date:</span>{' '}
                        <span className={classes.data}>
                            {new Date(member.BirthDate).toLocaleDateString()}
                        </span>
                    </span>
                    <span>
                        <span className={classes.label}>Patient ID:</span>{' '}
                        <span className={classes.data}>{member.PatientId}</span>
                    </span>
                </div>
            </div>
            <h2 className={classes.h2}>Reports</h2>
            <div className={classes.files}>
                {kitFiles.flatMap(({ KitId, Reports }) =>
                    Reports.map((report) => (
                        <FileCard
                            key={report.FilePath}
                            variant="report"
                            url={report.FilePath}
                            title={getKitDisplayName(
                                getKitById(kits, KitId),
                                kitTypes
                            )}
                            date={report.ReleaseDate}
                            isAdmin={isAdmin}
                            fileName={report.FileName}
                        />
                    ))
                )}
                {kitFiles.every((file) => file.Reports.length === 0) && (
                    <p>No reports available</p>
                )}
            </div>
            <h2 className={classes.h2}>Raw Data</h2>
            <div className={classes.files}>
                {kitFiles.flatMap(({ KitId, RawDataFiles }) =>
                    RawDataFiles.map((rawDataFile) => (
                        <FileCard
                            key={rawDataFile.FilePath}
                            variant="raw"
                            url={rawDataFile.FilePath}
                            title={getKitDisplayName(
                                getKitById(kits, KitId),
                                kitTypes
                            )}
                            date={rawDataFile.ReleaseDate}
                            isAdmin={isAdmin}
                            fileName={rawDataFile.FileName}
                        />
                    ))
                )}
                {kitFiles.every((file) => file.RawDataFiles.length === 0) && (
                    <p>No raw data available</p>
                )}
            </div>
        </Modal>
    );
};

const useStyles = makeStyles((theme) => ({
    h2: {
        color: 'theme.palette.primary.main',
        fontFamily: 'Noto Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.36px',
        marginBottom: '-30px',
        marginTop: '-10px',
        padding: 0,
    },
    patientInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    h3: {
        display: 'flex',
        width: '691px',
        padding: '9px 45px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        color: theme.palette.primary.main,
        borderRadius: '8px 8px 0px 0px',
        background: `linear-gradient(270deg, #E4FEFF 0%, #D6E6FF 100%)`,
        fontFamily: 'Poppins',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '150%',
        letterSpacing: '-0.1px',
        marginBottom: '0px',
    },
    details: {
        display: 'flex',
        width: '691px',
        height: '60px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        borderRadius: '0px 0px 8px 8px',
        background: theme.palette.background.default,
    },
    label: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
    },
    data: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
    },
    files: {
        display: 'flex',
        alignItems: 'center',
        gap: '31px',
        alignSelf: 'stretch',
        flexWrap: 'wrap',
    },
}));

export default ReportsAndRawDataModal;
